<script setup>
import { ref, computed, reactive, nextTick } from 'vue'
import BcsDynamicText from '../BcsDynamicText.vue'
import DatePicker from '../DatePicker.vue'
import BcsDropdown from '../BcsDropdown.vue'
import EmailInput from './EmailInput.vue'
import { 
  formattedEventDate, 
  formatDateToSendReminder,
  calculateDaysDifference,
  subtractDaysFromDate,
  getTodayDate,
  formatDateGoogleReminders
} from '../Reminders/dateUtils'
import { RequestService } from '../../services/RequestService'

const props = defineProps({
  eventDefinition: { type: Object, default: () => ({}) },
  filteredEvents: { type: Array, required: true },
  modalReminderSource: { type: String, required: true },
  reminderDaysOption: { type: Array, required: true },
  sortDirectionEvents: { type: String, required: true },
  recipients: { type: Array, required: true },
  actualEmployer: { type: Object, required: true },
  actualPlanYear: { type: Object, required: true },
  yearOfActualPlan: { type: String, required: true }
})

const { handleErrors } = RequestService()

const emit = defineEmits([
  'sendReminders',
  'toggleSortEvents',
  'handleCompleteTextUpdate',
  'close'
])

const selectedDateCalendar = ref(null)
const selectedDays = ref('')
const showEventDefinition = ref({ show: false, event: '' })
const showFullDescriptionSingleModal = ref(false)
const selectAllEvents = ref(false)
const selectedRecipients = ref([])
const validEmails = ref([])
const newEmail = ref('')
const scrollPosition = ref(0)
const descriptionEvent = ref('')
const hasValidEmailTyped = ref(false)
const todayDate = getTodayDate()

const stateModal = reactive({
  currentStep: 'reminder'
})

const formatEventDate = (dateString, excludeOptions = {}) => {
  return formattedEventDate(dateString, excludeOptions, props.actualPlanYear, props.yearOfActualPlan)
}

const isCustomDateSelection = computed(() => {
  const result = props.modalReminderSource === 'OffCanvas' ? 
    selectedDays.value === -1 && stateModal.currentStep === 'reminder' :
    hasCustomDateReminder.value && stateModal.currentStep === 'reminder'
  return result
})

const hasCustomDateReminder = computed(() => {
  return props.filteredEvents.some((event) => event.reminder === -1)
})

const selectedCustomDateEvent = computed(() => {
  return props.filteredEvents.find((event) => event.reminder === -1) || {}
})

const selectedEventsCount = computed(() => {
  return props.filteredEvents.filter((event) => event.selected).length
})

const allSelectedEventsHaveReminder = computed(() => {
  return props.filteredEvents
    .filter((event) => event.selected)
    .every((event) => event.reminder !== '')
})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isValidEmail = computed(() => {
  return newEmail.value.length > 0 && emailRegex.test(newEmail.value)
})

const computedFooterModalButton = computed(() => {
  let result;
  switch (stateModal.currentStep) {
    case 'recipients':
      result = {
        name: 'Preview Reminders >',
        click: 'preview'
      }
      break
    case 'preview':
      result = {
        name: 'Send Reminders',
        click: ''
      }
      break
    default:
      result = {
        name: 'Preview Reminder',
        click: 'preview'
      }
  }
  return result
})

const singleEventDropdownOptions = computed(() => {
  const filteredOptions = [...props.reminderDaysOption]
  
  if (props.eventDefinition?.customReminder !== null && 
      props.eventDefinition?.customReminder !== undefined && 
      selectedDays.value !== -1) {
    
    const customDays = props.eventDefinition.customReminder
    const customText = customDays === 1 ? 
      'Custom: 1 day before' : 
      `Custom: ${customDays} days before`
    
    filteredOptions.splice(-1, 0, {
      value: customDays,
      text: customText,
      isCustom: true
    })
  }
  
  return filteredOptions
})

const getDynamicOptionsForEvent = (event) => {
  const filteredOptions = [...props.reminderDaysOption]
  
  if (event.customReminder !== null && event.customReminder !== undefined && event.reminder === -2) {
    const customText = event.customReminder === 1 ? 
      'Custom: 1 day before' : 
      `Custom: ${event.customReminder} days before`
    
    filteredOptions.splice(-1, 0, {
      value: -2,
      text: customText,
      isCustom: true
    })
  }
  return filteredOptions
}

function clearModal() {  
  validEmails.value = []
  newEmail.value = ''
  selectedDays.value = ''
  selectedRecipients.value = []
  hasValidEmailTyped.value = false
  stateModal.currentStep = 'reminder'
  
  props.filteredEvents.forEach((event) => {
    event.selected = false
    event.reminder = ''
    event.customReminder = null
  })

  selectAllEvents.value = false
  selectedDateCalendar.value = null

  showEventDefinition.value = {
    show: false,
    definition: ''
  }
}

function insertDefaultEmails() {
  if (props.actualEmployer && props.actualEmployer.contact_emails) {
    validEmails.value = props.actualEmployer.contact_emails
      .split(',')
      .map((email) => email.trim())
  }
}

function toggleAllEvents() {
  const newSelectedState = selectAllEvents.value
  props.filteredEvents.forEach((event) => {
    event.selected = newSelectedState
  })
}

function updateSelectAllEvents() {
  selectAllEvents.value = props.filteredEvents.every((event) => event.selected)
}

const handleSelectChangeSingle = (option) => {
  const selectedValue = option.value
  selectedDays.value = selectedValue
  
  if (selectedValue === -1) {
    selectedDateCalendar.value = null
  }
}

const handleSelectChange = (option, event) => {  
  if (!event) {
    handleErrors('handleSelectChange', new Error('No event provided'))
    return
  }
  const selectedValue = option.value
  if (selectedValue === -1) {
    event.showCustom = true
    event.reminder = -1
    selectedCustomDateEvent.value = { ...event }      
    selectedDateCalendar.value = event.customDate || null
    
    const eventElement = document.getElementById(`event-${event.id}`)
    if (eventElement) {
      scrollPosition.value = eventElement.offsetTop
    }
  } else if (selectedValue !== -2) {
    event.showCustom = false
    event.reminder = selectedValue
    event.customReminder = null
    event.customDate = null
    selectedCustomDateEvent.value = null
    selectedDateCalendar.value = null
  }

  const index = props.filteredEvents.findIndex((e) => e.id === event.id)
  if (index !== -1) {
    Object.assign(props.filteredEvents[index], event)
  } else {
    handleErrors('Event not found in filteredEvents:', event.id)
  }
}

function handleShowDefinitionModal(event) {
  showEventDefinition.value = {
    show: true,
    event: event
  }
}

const handleCancelCustomDate = () => {
  selectedDateCalendar.value = null
  selectedCustomDateEvent.value = null
  stateModal.currentStep = 'reminder'

  if (props.modalReminderSource === 'calendar') {
    props.filteredEvents.forEach((event) => {
      if (event.reminder === -1) {
        event.reminder = ''
        event.customReminder = null
        event.customDate = null
        event.showCustom = false
      }
    })
  } else if (props.modalReminderSource === 'OffCanvas') {
    if (props.eventDefinition.customReminder !== undefined && props.eventDefinition.customReminder !== null) {
      selectedDays.value = props.eventDefinition.customReminder
    } else {
      selectedDays.value = ''
    }
  }
}

const handleCustomDateSelection = () => {
  if (selectedDateCalendar.value && selectedCustomDateEvent.value) {
    const event = selectedCustomDateEvent.value
    const dueDate = formatEventDate(event.date)
    const reminderDate = selectedDateCalendar.value
    const diffDays = calculateDaysDifference(dueDate, reminderDate)

    const updatedEvent = {
      ...event,
      customReminder: diffDays,
      reminder: -2,
      customDate: selectedDateCalendar.value,
      showCustom: true
    }

    const index = props.filteredEvents.findIndex((e) => e.id === event.id)
    if (index !== -1) {
      Object.assign(props.filteredEvents[index], updatedEvent)

      nextTick(() => {
        const tableContainer = document.querySelector('.table-container-events')
        if (tableContainer) {
          tableContainer.scrollTop = scrollPosition.value - 50
        }
      })
    }
  }

  selectedDateCalendar.value = null
  selectedCustomDateEvent.value = null
}

const handleSingleEventCustomDate = () => {
  const diffDays = calculateDaysDifference(
    formatEventDate(props.eventDefinition.date),
    selectedDateCalendar.value
  )
  // eslint-disable-next-line vue/no-mutating-props
  props.eventDefinition.customReminder = diffDays
  selectedDays.value = diffDays
  selectedDateCalendar.value = null
  stateModal.currentStep = 'reminder'
  return
}

const handleClickFooterModal = () => {

  if (stateModal.currentStep === 'preview') {
    if (newEmail.value && isValidEmail.value) {
      addEmail(newEmail.value)
      newEmail.value = ''
    }
    
    const selectedEventsWithCorrectValues = props.filteredEvents
      .filter(event => event.selected === true)
      .map(event => ({
        ...event,
        reminder: event.reminder,
        selected: event.selected,
        customReminder: event.customReminder,
        customDate: event.customDate,
        showCustom: event.showCustom
      }))
  
    const remindersData = {
      selectedRecipients: selectedRecipients.value,
      validEmails: validEmails.value,
      selectedDays: props.modalReminderSource === 'OffCanvas' ? selectedDays.value : null,
      filteredEvents: selectedEventsWithCorrectValues,
      eventDefinition: props.eventDefinition,
      descriptionEvent: descriptionEvent.value,
      modalReminderSource: props.modalReminderSource
    }
    
    emit('sendReminders', remindersData)
    return
  }

  if (isCustomDateSelection.value && stateModal.currentStep === 'reminder') {
    if (props.modalReminderSource === 'calendar') {
      handleCustomDateSelection()
    } else if (props.modalReminderSource === 'OffCanvas') {
      handleSingleEventCustomDate()
    }
    return
  }

  if (stateModal.currentStep === 'recipients') {
    if (newEmail.value && isValidEmail.value) {
      addEmail(newEmail.value)
      newEmail.value = ''
    }
    stateModal.currentStep = 'preview'
    return
  }

  if (stateModal.currentStep === 'reminder') {
    if (newEmail.value && isValidEmail.value) {
      addEmail(newEmail.value)
      newEmail.value = ''
    }
    stateModal.currentStep = 'recipients'
    return
  }
  
}

function addEmail(email) {
  if (email !== '') {
    validEmails.value.push(email)
  }
}

function toggleSortEvents() {
  emit('toggleSortEvents')
}

function handleCompleteTextUpdate(newValue) {
  descriptionEvent.value = newValue
  emit('handleCompleteTextUpdate', newValue)
}

function handleCancel() {
  clearModal()
}

function handleValidEmailTyped(isValid) {
  hasValidEmailTyped.value = isValid
}

defineExpose({
  clearModal,
  insertDefaultEmails,
  stateModal
})
</script>

<template>
  <div
    class="modal fade modal-event"
    id="addDueDateModal"
    tabindex="-1"
    aria-labelledby="addDueDateModalLabel"
    aria-hidden="true"
    style="--bs-modal-width: 919px"
    data-bs-backdrop="static"
    data-bs-keyboard="false">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header px-4 d-flex flex-column bg-gray-200">
          <div class="d-flex w-100 justify-content-between">
            <span class="modal-title header-22" id="addDueDateModalLabel">
              <div v-if="stateModal.currentStep === 'reminder' && !showEventDefinition.show">
                <i class="bi bi-alarm"></i>
                <strong>Add Due Date Reminders to External Calendar</strong>
              </div>

              <div v-if="stateModal.currentStep === 'reminder' && showEventDefinition.show">
                <i class="bi bi-journal-bookmark-fill"></i>
                <strong>Definition</strong>
              </div>

              <div v-if="stateModal.currentStep === 'recipients'">
                <i class="bi bi-person-plus-fill"></i>
                <strong>Add Recipients of Due Date Reminder</strong>
              </div>

              <div v-if="stateModal.currentStep === 'preview'">
                <i class="bi bi-eye"></i>
                <strong>Preview Due Date Reminder</strong>
              </div>
            </span>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"></button>
          </div>
          <p
            v-if="!showFullDescriptionSingleModal && !showEventDefinition.show"
            class="me-auto mb-0 text-16 text-gray-700 mt-1">
            <BcsDynamicText :text="'calendarDescriptionAddReminderText'" />
          </p>
        </div>

        <!-- Body -->
        <div class="modal-body" :style="{ 'margin-bottom': showEventDefinition.show ? '20px' : '' }">
          <div
            v-if="stateModal.currentStep == 'preview'"
            class="preview-body-title"
            style="margin-top: 7px; margin-bottom: 10px">
            Reminders will show as an all day event or task, usually at the top of the recipient's
            calendar day.
          </div>

          <!--  reminder modal : opt1 (multiple) -->
          <div v-if="stateModal.currentStep === 'reminder' && modalReminderSource == 'calendar'">
            <div
              v-if="hasCustomDateReminder"
              class="d-flex justify-content-center text-center calendar">
              <div>
                <h4>Custom Reminder Date Selected: {{ selectedDateCalendar }}</h4>
                <h5>{{ selectedCustomDateEvent?.name }}</h5>

                <p>Due Date: {{ formatEventDate(selectedCustomDateEvent?.date) }}</p>

                <div class="d-flex justify-content-center custom-datepicker-container">
                  <DatePicker
                    v-model:selectedDate="selectedDateCalendar"
                    :dueDate="selectedCustomDateEvent?.date"
                    :contextualPlanYear="props.actualPlanYear" />
                </div>
              </div>
            </div>

            <div v-else :class="['table-container-events', { card: !showEventDefinition.show }]">
              <table v-if="!showEventDefinition.show" class="table table-borderless">
                <thead class="thead-custom-red">
                  <tr>
                    <th scope="col" class="" style="padding-left: 20px">
                      <input
                        v-model="selectAllEvents"
                        @change="toggleAllEvents"
                        class="form-check-input text-16"
                        type="checkbox" />
                    </th>
                    <th scope="col" class="second-column text-secondary">Item</th>
                    <th scope="col" class="third-column text-secondary">
                      Due

                      <i
                        @click="toggleSortEvents"
                        :class="{
                          'bi-sort-down': sortDirectionEvents === 'asc',
                          'bi-sort-up': sortDirectionEvents === 'desc'
                        }"
                        style="padding-left: 5px; color: var(--bs-primary); cursor: pointer"
                        class="bi"></i>
                    </th>
                    <th scope="col" class="text-secondary">Reminder (business days)</th>
                    <th scope="col" class="text-secondary">Reminder Date</th>
                  </tr>
                </thead>

                <tbody class="text-14">
                  <tr v-for="event in filteredEvents" :key="event.id" :id="`event-${event.id}`">
                    <td style="padding-left: 20px">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        v-model="event.selected"
                        @change="updateSelectAllEvents" />
                    </td>
                    <td
                      @click="handleShowDefinitionModal(event)"
                      style="color: var(--bs-primary); cursor: pointer">
                      <strong>{{ event.name }}</strong>
                    </td>
                    <td :style="{ fontWeight: event.selected ? 700 : 'normal' }" class="text-secondary">
                      {{ formatEventDate(event.date) }}
                    </td>
                    <td>
                      <BcsDropdown
                        :dropdown-options="getDynamicOptionsForEvent(event)"
                        v-model="event.reminder"
                        @change="(option) => handleSelectChange(option, event)"
                        :context="event" />
                    </td>

                    <td :style="{ fontWeight: event.selected ? 700 : 'normal' }" class="text-secondary">
                      <div v-if="event.reminder === 0">
                        {{ formatDateGoogleReminders(todayDate) }}
                      </div>
                      <div v-else>
                        {{
                          event.reminder !== '' && event.customReminder == null ?
                            formatDateGoogleReminders(
                              subtractDaysFromDate(
                                formatDateToSendReminder(event.date, actualPlanYear, yearOfActualPlan),
                                event.reminder
                              )
                            )
                          : event.customReminder !== null && event.reminder == -2 ?
                            formatDateGoogleReminders(
                              subtractDaysFromDate(
                                formatDateToSendReminder(event.date, actualPlanYear, yearOfActualPlan),
                                event.customReminder
                              )
                            ) 
                          : '--' 
                        }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- DEFINITION MULTIPLES EVENTS -->
              <div v-else-if="showEventDefinition.show">
                <div
                  style="margin-top: 13px; padding-left: 26px"
                  class="back-to-main d-flex"
                  @click="showEventDefinition.show = false">
                  <i class="bi bi-chevron-left"></i>
                  Back to Main Reminder Selection
                </div>

                <div :style="{ padding: showEventDefinition.show ? '30px' : '' }">
                  <h5>{{ showEventDefinition.event.name }}</h5>
                  <p>Due Date: {{ formatEventDate(showEventDefinition.event.date) }}</p>

                  <div class="text-14" style="padding-left: 4px">
                    <BcsDynamicText :text="showEventDefinition.event.body"></BcsDynamicText>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- reminder modal: opt2 ( single) -->
          <div
            v-if="stateModal.currentStep === 'reminder' && modalReminderSource == 'OffCanvas'"
            class="">
            <div v-if="!showFullDescriptionSingleModal && selectedDays !== -1">
              <div class="calendar-moda-item-single">
                <div class="calendar-moda-item-single__header">
                  <div class="calendar-moda-item-single__header-txt">Item</div>

                  <div class="calendar-moda-item-single__header-txt">Due</div>

                  <div class="calendar-moda-item-single__header-txt">Reminder</div>
                </div>

                <div class="calendar-moda-item-single__body text-14">
                  <div class="calendar-moda-item-single__body-info">
                    <div class="calendar-moda-item-single__body-info-txt">
                      <strong>{{ eventDefinition.name }}</strong>
                    </div>

                    <div class="calendar-moda-item-single__body-info-txt">
                      {{ formatEventDate(eventDefinition.date) }}
                    </div>

                    <div class="calendar-moda-item-single__body-info-txt">
                      <BcsDropdown
                        :dropdown-options="singleEventDropdownOptions"
                        v-model="selectedDays"
                        @change="(option) => handleSelectChangeSingle(option)"
                        :context="eventDefinition" />
                    </div>
                  </div>

                  <div class="calendar-moda-item-single__body-description">
                    <div class="truncate-text">
                      <BcsDynamicText
                        @update:completeText="handleCompleteTextUpdate"
                        :text="eventDefinition.body"></BcsDynamicText>
                    </div>

                    <div
                      @click="showFullDescriptionSingleModal = true"
                      class="calendar-moda-item-single__body-description-btn">
                      Read full description >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style="padding: 0 20px">
              <div
                v-if="showFullDescriptionSingleModal && selectedDays !== -1"
                class="show-information-single-modal">
                <div class="back-to-main d-flex" @click="showFullDescriptionSingleModal = false">
                  <i class="bi bi-chevron-left"></i>
                  Back to Main Reminder Selection
                </div>

                <div class="show-information-single-modal__full-information">
                  <h5>{{ eventDefinition.name }}</h5>
                  <p>Due Date: {{ formatEventDate(eventDefinition.date) }}</p>

                  <div class="text-14" style="padding-left: 4px">
                    <BcsDynamicText :text="eventDefinition.body"></BcsDynamicText>
                  </div>
                </div>
              </div>
            </div>

            <!-- show calendar -->
            <div
              v-if="selectedDays == -1"
              class="d-flex justify-content-center text-center calendar">
              <div>
                <h4>Custom Reminder Date Selected: {{ selectedDateCalendar }}</h4>
                <h5>{{ eventDefinition.name }}</h5>

                <p>Due Date: {{ formatEventDate(eventDefinition.date) }}</p>

                <div class="d-flex justify-content-center custom-datepicker-container">
                  <DatePicker
                    v-model:selectedDate="selectedDateCalendar"
                    :dueDate="eventDefinition.date"
                    :contextualPlanYear="props.actualPlanYear" />
                </div>
              </div>
            </div>
          </div>

          <!-- recipients modal -->
          <div class="recipients-modal" v-if="stateModal.currentStep === 'recipients'">
            <b>{{ selectedDays !== '' ? 'Reminder Date:' : 'Reminders:' }}</b>
            <p v-if="selectedDays !== ''" style="margin-top: 4px" class="text-14">
              Note: The date listed here represents the reminder for Form 8508, but you've also
              opted that a reminder be sent
              <span v-if="selectedDays == 0">today</span>
              <span v-else>
                {{ selectedDays }} business {{ selectedDays > 1 ? 'days' : 'day ' }} before
              </span>
              all items that are due for this plan year.
            </p>

            <div v-if="selectedDays !== ''" class="current-reminder text-14">
              <p>
                <span v-if="selectedDays == 0">Today</span>
                <span v-else>
                  {{ eventDefinition.customReminder ? eventDefinition.customReminder : selectedDays}} 
                  business 
                  {{ (eventDefinition.customReminder ? eventDefinition.customReminder : selectedDays) > 1 ? 'days' : 'day ' }} 
                  before
                </span>
                <span>{{ formatEventDate(eventDefinition.date) }}</span>
              </p>
              <button @click="stateModal.currentStep = 'reminder'" class="btn btn-info text-1">
                Change
              </button>
            </div>

            <div v-else class="current-reminders text-14">
              You've chosen
              <strong>{{ selectedEventsCount }}</strong>
              items to be reminded of from the BCS Compliance Calendar. To review those items, go
              back using the button below.
            </div>

            <div class="internal-recipients">
              <p class="text-14">
                <b>Internal Recipient(s):</b>
              </p>

              <div class="internal-recipients__options">
                <div
                  v-for="(recipient, index) in recipients"
                  :key="index"
                  class="internal-recipients__options-option">
                  <input
                    class="form-check-input text-16"
                    type="checkbox"
                    :id="'recipient-' + index"
                    :value="recipient"
                    v-model="selectedRecipients" />
                  <label class="form-check-label text-16" :for="'recipient-' + index">
                    {{ recipient.email }}
                  </label>
                </div>
              </div>
            </div>
            
            <div class="other-recipients text-14" style="margin-top: 20px">
              <b>Other Recipient(s)</b>

              <p style="margin-top: 3px">
                Type an email and press enter or separate with commas. Enter as many emails as
                needed.
              </p>

              <EmailInput 
                v-model="validEmails" 
                v-model:newEmail="newEmail"
                @validEmailTyped="handleValidEmailTyped" />
            </div>
          </div>

          <!-- preview modal -->
          <div class="preview-modal px-4.5" v-if="stateModal.currentStep === 'preview'">
            <div class="preview-modal__reminder-date fill-preview">
              <div class="preview-modal__reminder-date-title">
                <span class="title-preview" @click="stateModal.currentStep = 'reminder'">
                  <i class="bi bi-pencil-square"></i>
                  Edit
                </span>
                <b class="">{{ selectedDays !== '' ? 'Reminder:' : 'Reminders:' }}</b>
              </div>

              <div class="response-preview">
                <div v-if="selectedDays !== ''">
                  <span v-if="selectedDays == 0">Today</span>
                  <span v-else>
                    {{ selectedDays }} business {{ selectedDays > 1 ? 'days' : 'day ' }} before
                  </span>
                </div>
                <div style="color: var(--bs-gray-700)" v-else>
                  You've chosen
                  <strong>{{ selectedEventsCount }}</strong>
                  items to be reminded of from the BCS Compliance Calendar.
                </div>
              </div>
            </div>
            <div class="preview-modal__employer fill-preview">
              <b class="preview-modal__reminder-date-title pl-79">Employer:</b>
              <div>{{ actualEmployer.name }}</div>
            </div>

            <div class="preview-modal__recipients fill-preview">
              <div class="preview-modal__internal-recipients">
                <div class="preview-modal__reminder-date-title">
                  <span class="title-preview" @click="stateModal.currentStep = 'recipients'">
                    <i class="bi bi-pencil-square"></i>
                    Edit
                  </span>
                  <b class="">Internal Recipient(s):</b>
                </div>

                <div>
                  <div v-for="recipient in selectedRecipients" :key="recipient">
                    {{ recipient.email }}
                  </div>
                </div>
              </div>
              <div class="preview-modal__other-recipients" style="margin-top: 8px">
                <b class="preview-modal__reminder-date-title pl-79">Other Recipient(s):</b>

                <div class="other-emails-preview">
                  <div v-for="email in validEmails" :key="email">
                    {{ email }}
                  </div>
                  <div v-if="newEmail && isValidEmail" style="font-style: italic; color: var(--bs-gray-600)">
                    {{ newEmail }} (will be added)
                  </div>
                </div>
              </div>
            </div>

            <div class="preview-modal__from fill-preview">
              <b class="preview-modal__reminder-date-title pl-79">From:</b>
              <div><BcsDynamicText text="reminderEmailFrom"></BcsDynamicText></div>
            </div>
            <div class="preview-modal__title fill-preview" v-if="selectedDays !== ''">
              <b class="preview-modal__reminder-date-title pl-79">Title:</b>
              <div>
                {{ eventDefinition.name }} is Due in {{ selectedDays }}
                {{ selectedDays > 1 ? 'days' : 'day ' }}
              </div>
            </div>
            <div class="preview-modal__message" style="padding-bottom: 12px; padding-top: 12px">
              <b class="preview-modal__reminder-date-title pl-79">
                {{ selectedDays !== '' ? 'Message:' : 'Title & Message:' }}
              </b>

              <div v-if="selectedDays !== ''" class="truncate-text">
                Good day! This is a reminder that
                <BcsDynamicText :text="eventDefinition.body"></BcsDynamicText>
              </div>

              <div v-else>
                <BcsDynamicText text="titleMessagePreviewReminder"></BcsDynamicText>
              </div>
            </div>
          </div>
        </div>

        <!-- footer modal 2 buttons/3 buttons -->
        <div
          v-if="
            stateModal.currentStep === 'reminder' &&
            !showFullDescriptionSingleModal &&
            stateModal.currentStep === 'reminder' &&
            !isCustomDateSelection &&
            !showEventDefinition.show
          "
          class="modal-footer d-flex justify-content-end">
          <button
            type="button"
            class="btn btn-outline-primary"
            data-bs-dismiss="modal"
            @click="handleCancel">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="
              (selectedDays === '' && selectedEventsCount < 1) ||
              (selectedEventsCount > 0 && !allSelectedEventsHaveReminder)
            "
            @click="stateModal.currentStep = 'recipients'">
            Choose Recipients >
          </button>
        </div>

        <div
          v-if="
            stateModal.currentStep === 'recipients' ||
            stateModal.currentStep === 'preview' ||
            (stateModal.currentStep === 'reminder' && isCustomDateSelection)
          "
          class="modal-footer d-flex justify-content-between align-items-center">
          <div>
            <button
              @click="
                !isCustomDateSelection ?
                  (stateModal.currentStep = 'reminder')
                : handleCancelCustomDate()
              "
              v-if="stateModal.currentStep === 'recipients' || isCustomDateSelection"
              type="button"
              :class="[!isCustomDateSelection ? 'btn-info back-to-reminder-btn btn' : 'go-back']">
              <span>&lt;</span>
              {{ !isCustomDateSelection ? 'Back to Reminder Selection' : 'Cancel and Go Back' }}
            </button>
          </div>
          <div>
            <button
              v-if="!isCustomDateSelection"
              type="button"
              class="btn btn-outline-primary me-2"
              data-bs-dismiss="modal">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="
                (stateModal.currentStep === 'recipients' &&
                  selectedRecipients.length === 0 &&
                  !hasValidEmailTyped &&
                  validEmails.length === 0) ||
                (newEmail.length !== 0 && !isValidEmail) ||
                (isCustomDateSelection && !selectedDateCalendar)
              "
              :data-bs-dismiss="stateModal.currentStep === 'preview' ? 'modal' : null"
              @click="handleClickFooterModal">
              <i
                v-if="stateModal.currentStep === 'preview'"
                class="bi bi-calendar-check-fill"></i>
              {{ (isCustomDateSelection && stateModal.currentStep === 'reminder') ? 'Choose Custom Date' : computedFooterModalButton.name }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal-event {
  background-color: rgba(0, 0, 0, 0.7);
}

.modal-title {
  i {
    margin-right: 11px;
  }
}

.bg-gray-200 {
  background-color: var(--bs-gray-200);
}

.preview-body-title {
  display: flex;
  align-items: center;
  gap: 60px;
}

.form-select {
  --bs-form-select-bg-img: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%235C0CB8' class='bi bi-caret-down-fill' viewBox='0 0 16 16'%3e%3cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3e%3c/svg%3e");
  border-color: var(--bs-gray-600);
  color: var(--bs-gray-600);
  width: 195px;
}

.current-reminder {
  border: 1px solid var(--bs-gray-300);
  padding: 12px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    margin-left: 34px;
  }

  p {
    margin: 0;
    padding: 0;
  }
}

.internal-recipients {
  margin-top: 20px;

  &__options {
    &-option {
      margin-bottom: 8px;

      input {
        margin-right: 8px;
      }
    }
  }
}

.other-recipients {
  &__emails {
    border: 1px solid var(--bs-gray-700);
    padding: 10px;
    border-radius: 4px;
  }
}

.preview-modal {
  &__title,
  &__reminder-date,
  &__message,
  &__internal-recipients,
  &__other-recipients,
  &__employer,
  &__from {
    display: flex;
    font-size: 14px;
  }
}

.preview-modal__reminder-date-title {
  min-width: 240px;
}

.pl-79 {
  padding-left: 79px;
}

.fill-preview {
  border-bottom: 1px solid var(--bs-gray-400);
  padding-top: 12px;
  padding-bottom: 12px;
}

.truncate-text {
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-preview {
  padding-right: 26px;
  color: var(--bs-primary);
  font-weight: 700;
  cursor: pointer;

  i {
    padding-right: 5px;
  }
}

.thead-custom-red th {
  background-color: var(--bs-gray-100);
  border-radius: 4px;
  border-bottom: 1px solid var(--bs-gray-400);
}

.second-column {
  width: 250px !important;
}

.third-column {
  width: 190px !important;
}

.current-reminders {
  color: var(--bs-gray-700);
}

.recipients-modal {
  padding: 0 40px;
}

.border-bottom {
  border-bottom: 1px solid #c5cdd4;
}

.go-back {
  color: var(--bs-primary);
  font-weight: 700;
  cursor: pointer;
  font-size: 14px;
  background: none;
  border: none;
}

.calendar {
  margin-top: 12px;
  margin-bottom: 12px;
  h4 {
    font-size: 20px;
  }
  h5,
  p {
    font-size: 16px;
  }

  h5,
  h4 {
    font-weight: 700;
  }

  p {
    font-weight: 400;
  }
}

.table-container-events {
  height: auto;
  height: 364px;
  overflow-y: scroll;
}

.back-to-main {
  color: var(--bs-primary);
  font-weight: 700;
  cursor: pointer;
  font-size: 14px;

  i {
    padding-right: 5px;
  }
}

.back-to-reminder-btn {
  border-color: var(--bs-primary);

  span {
    color: #212529;
  }
}

.calendar-moda-item-single {
  border: 1px solid var(--bs-gray-400);
  border-radius: 4px;

  &__header {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    font-size: 16px;
    font-weight: 700;
    background-color: var(--bs-gray-100);
    padding: 10px 0 10px 20px;
    border-bottom: 1px solid var(--bs-gray-400);
  }

  &__body {
    &-info {
      display: grid;
      align-items: center;
      padding: 10px 0 10px 20px;
      grid-template-columns: repeat(3, 1fr);

      select {
        width: 195px;
      }
    }

    &-description {
      padding: 0px 250px 9px 20px;

      &-btn {
        margin-top: 20px;
        font-weight: 700;
        color: var(--bs-primary);
        cursor: pointer;
        margin-bottom: 14px;
      }
    }
  }
}

.show-information-single-modal {
  padding-top: 13px;
  padding-bottom: 29px;

  &__full-information {
    margin-top: 29px;
  }
}

.not-valid {
  border-color: var(--bs-form-invalid-border-color);
  padding-right: calc(1.5em + 0.75rem);
}
</style>